import React, { Component } from 'react';
import withStyles from '@mui/styles/withStyles';
import IconButton from '@mui/material/IconButton';
import Grid from './Grid';
import Select from './Select';
import TextField from './TextField';
import PrimaryButton from './PrimaryButton';
import DeleteIcon from '../icon/DeleteIcon';
import { InlineHeader } from './Inline';
import ValidationPlaceholder from './ValidationPlaceholder';
import { messages, validationHelper } from '../../util/validation';

export const PHONE_TYPE_OPTIONS = [
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Home', label: 'Home' },
    { value: 'Work', label: 'Work' },
    { value: 'Other', label: 'Other' }
];

export const listedPhones = phones =>
    (phones || []).filter(phone => !!phone && !!String(phone.Number || '').trim());

export const primaryPhoneNumber = phones => {
    const listed = listedPhones(phones);
    return listed.length ? listed[0].Number : null;
};

export const phoneKey = value => {
    const digits = ('' + (value || '')).replace(/\D/g, '');
    return digits.length < 8 ? '' : digits.slice(-8);
};

export const matchesPhone = (phones, value) => {
    const key = phoneKey(value);
    if (!key) return false;
    return listedPhones(phones).some(phone => phoneKey(phone.Number) === key);
};

export const validatePhoneNumber = value => {
    if (!value) return validationHelper.ok();

    const digits = ('' + value)
        .trim()
        .replace(/^\+/, '')
        .replace(/[\s\-().]/g, '');

    if (!/^\d+$/.test(digits)) return validationHelper.required('Enter a single number, digits only');
    if (digits.length < 8 || digits.length > 15) return validationHelper.required('Enter a valid phone number');

    return validationHelper.ok();
};

class PhoneNumberList extends Component {
    getPhones() {
        const { form, name } = this.props;
        return (form ? form.getField(name) : null) || [];
    }

    selectableType(value) {
        return PHONE_TYPE_OPTIONS.some(option => option.value === value) ? value : '';
    }

    setPhones(phones) {
        const { form, name } = this.props;
        form.setField({ [name]: phones.map((phone, index) => ({ ...phone, Sort: index })) });
    }

    onAdd = () => {
        this.setPhones([...this.getPhones(), { Number: '', Type: 'Mobile', Comment: '' }]);
    };

    onDelete = index => {
        this.setPhones(this.getPhones().filter((phone, i) => i !== index));
    };

    onChangeType = (index, value) => {
        const { form, name } = this.props;
        if (!value) form.setField({ [`${name}[${index}].Type`]: 'Unknown' });
    };

    hasAnyNumber() {
        return this.getPhones().some(phone => !!phone && !!String(phone.Number || '').trim());
    }

    render() {
        const { form, name, label, disabled, addLabel, required, validationResult, classes } = this.props;
        const phones = this.getPhones();
        const requiredResult =
            required && !this.hasAnyNumber()
                ? validationHelper.required(messages.atLeastOne('phone number'))
                : validationHelper.ok();
        const blockValidation = requiredResult.valid && validationResult ? validationResult : requiredResult;

        return (
            <Grid pc={1}>
                <ValidationPlaceholder validationResult={blockValidation}>
                    {!!label && (
                        <Grid pc={1}>
                            <InlineHeader header={label} />
                        </Grid>
                    )}

                    {phones.map((phone, index) => (
                        <Grid container spacing={2} key={phone.ID || `new-${index}`}>
                            <Grid pc={0.42}>
                                <TextField
                                    disabled={disabled}
                                    name={`${name}[${index}].Number`}
                                    label="Number"
                                    form={form}
                                    validationResult={validatePhoneNumber(
                                        form.getField(`${name}[${index}].Number`)
                                    )}
                                />
                            </Grid>
                            <Grid pc={0.17}>
                                <Select
                                    disabled={disabled}
                                    name={`${name}[${index}].Type`}
                                    label="Type (optional)"
                                    form={form}
                                    value={this.selectableType(form.getField(`${name}[${index}].Type`))}
                                    options={PHONE_TYPE_OPTIONS}
                                    placeholder="Select type..."
                                    onChange={(e, value) => this.onChangeType(index, value)}
                                />
                            </Grid>
                            <Grid pc={0.25}>
                                <TextField
                                    disabled={disabled}
                                    name={`${name}[${index}].Comment`}
                                    label="Comment"
                                    form={form}
                                />
                            </Grid>
                            <Grid pc={0.17}>
                                <IconButton
                                    className={classes.deleteButton}
                                    disabled={disabled}
                                    onClick={() => this.onDelete(index)}
                                    title="Remove this number"
                                    size="large"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}

                    <Grid pc={1}>
                        <PrimaryButton onClick={this.onAdd} disabled={disabled}>
                            {addLabel || '+ Add Phone Number'}
                        </PrimaryButton>
                    </Grid>
                </ValidationPlaceholder>
            </Grid>
        );
    }
}

const styles = () => ({
    deleteButton: {
        marginTop: 10
    }
});

export default withStyles(styles)(PhoneNumberList);
